import { Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getOthWelcomeEmail,
  updateOthWelcomeEmail,
  updateOthWelcomeEmailDefault,
} from 'src/api/othRegistration';
import { MuiModal } from 'src/components/material/MuiModal';

import styles from './EditWelcomeEmailModal.module.scss';
import {
  DEFAULT_WELCOME_EMAIL_BODY,
  DEFAULT_WELCOME_EMAIL_SUBJECT,
  findUnknownWelcomeEmailTokens,
  previewWelcomeEmail,
  WELCOME_EMAIL_TOKENS,
} from './welcomeEmail';

type EditWelcomeEmailModalProps = {
  open: boolean;
  circleId: string;
  circleTitle?: string;
  sampleName?: string;
  onClose: () => void;
};

type FieldName = 'subject' | 'body';

const getBackendErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      { message?: string; error?: string } | undefined;
    if (data?.message || data?.error) {
      return data.message || data.error || 'Could not save welcome email.';
    }

    if (error.response?.status === 403) {
      return 'You must be a leader or co-leader to edit the welcome email.';
    }

    if (error.response?.status === 404) {
      return 'Welcome email is not available on the server yet.';
    }
  }

  return 'Could not save welcome email. Please try again.';
};

const isMissingTemplate = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

export const EditWelcomeEmailModal = ({
  open,
  circleId,
  circleTitle,
  sampleName,
  onClose,
}: EditWelcomeEmailModalProps) => {
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFieldRef = useRef<FieldName>('body');

  const [isLoading, setIsLoading] = useState(false);
  const [isSavingCircle, setIsSavingCircle] = useState(false);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subject, setSubject] = useState(DEFAULT_WELCOME_EMAIL_SUBJECT);
  const [body, setBody] = useState(DEFAULT_WELCOME_EMAIL_BODY);
  const [savedSubject, setSavedSubject] = useState(
    DEFAULT_WELCOME_EMAIL_SUBJECT
  );
  const [savedBody, setSavedBody] = useState(DEFAULT_WELCOME_EMAIL_BODY);

  const loginUrl = `${window.location.origin}/login`;
  const previewValues = useMemo(
    () => ({
      navn: sampleName?.trim() || 'Ole',
      program: circleTitle?.trim() || 'Lederprogrammet',
      innlogging: loginUrl,
    }),
    [circleTitle, loginUrl, sampleName]
  );

  const unknownTokens = useMemo(
    () => findUnknownWelcomeEmailTokens(`${subject}\n${body}`),
    [body, subject]
  );

  const isDirty = subject !== savedSubject || body !== savedBody;
  const canSave =
    subject.trim().length > 0 && body.trim().length > 0 && isDirty;

  const applyTemplate = (nextSubject: string, nextBody: string) => {
    setSubject(nextSubject);
    setBody(nextBody);
    setSavedSubject(nextSubject);
    setSavedBody(nextBody);
  };

  const loadTemplate = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const result = await getOthWelcomeEmail(circleId);
      applyTemplate(
        result.subject?.trim() || DEFAULT_WELCOME_EMAIL_SUBJECT,
        result.body?.trim() || DEFAULT_WELCOME_EMAIL_BODY
      );
    } catch (error) {
      applyTemplate(DEFAULT_WELCOME_EMAIL_SUBJECT, DEFAULT_WELCOME_EMAIL_BODY);
      if (!isMissingTemplate(error)) {
        setLoadError(
          'Could not load the saved welcome email. Showing the default text.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [circleId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    lastFieldRef.current = 'body';
    void loadTemplate();
  }, [loadTemplate, open]);

  const insertToken = (token: string) => {
    const subjectEl = subjectRef.current;
    const bodyEl = bodyRef.current;
    const focused: FieldName | null =
      document.activeElement === subjectEl
        ? 'subject'
        : document.activeElement === bodyEl
          ? 'body'
          : null;
    const target = focused ?? lastFieldRef.current;
    const el = target === 'subject' ? subjectEl : bodyEl;

    if (!el) {
      return;
    }

    const hasSelection = focused !== null || document.activeElement === el;
    const start = hasSelection
      ? (el.selectionStart ?? el.value.length)
      : el.value.length;
    const end = hasSelection ? (el.selectionEnd ?? start) : start;
    const next = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`;
    const cursor = start + token.length;

    if (target === 'subject') {
      setSubject(next);
    } else {
      setBody(next);
    }

    lastFieldRef.current = target;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  };

  const isSaving = isSavingCircle || isSavingDefault;
  const payload = {
    subject: subject.trim(),
    body: body.trim(),
  };

  const handleSaveForCircle = async () => {
    if (!canSave) {
      return;
    }

    try {
      setIsSavingCircle(true);
      const result = await updateOthWelcomeEmail(circleId, payload);
      applyTemplate(
        result.subject?.trim() || payload.subject,
        result.body?.trim() || payload.body
      );
      toast.success(
        'Saved for this circle. New registrations here will receive it.'
      );
      onClose();
    } catch (error) {
      toast.error(getBackendErrorMessage(error));
    } finally {
      setIsSavingCircle(false);
    }
  };

  const handleSaveAsDefault = async () => {
    if (!canSave) {
      return;
    }

    const confirmed = window.confirm(
      'Save this text as the default welcome email?\n\nIt will be used for all OTH circles that do not have their own welcome text. Circles with custom welcome text are unchanged.'
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsSavingDefault(true);
      const result = await updateOthWelcomeEmailDefault(payload);
      applyTemplate(
        result.subject?.trim() || payload.subject,
        result.body?.trim() || payload.body
      );
      toast.success(
        'Saved as default for circles without their own welcome text.'
      );
      onClose();
    } catch (error) {
      toast.error(getBackendErrorMessage(error));
    } finally {
      setIsSavingDefault(false);
    }
  };

  return (
    <MuiModal
      open={open}
      setOpen={(nextOpen) => !nextOpen && onClose()}
      width={760}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Edit welcome email</h2>
          <p className={styles.subtitle}>
            This is sent automatically when someone completes registration.
            Saving does not send anything now. Choose whether to save for this
            circle only, or as the default for circles without their own welcome
            text.
          </p>
        </div>

        {isLoading ? (
          <div className={styles.loading}>
            <CircularProgress size={26} />
          </div>
        ) : (
          <>
            <div className={styles.help}>
              <p className={styles.helpText}>
                Click a code to insert it where the cursor is.{' '}
                <strong>[navn]</strong> becomes first name,{' '}
                <strong>[program]</strong> becomes the circle title, and{' '}
                <strong>[innlogging]</strong> becomes the Dunbar login link.
              </p>
              <div className={styles.tokens}>
                {WELCOME_EMAIL_TOKENS.map(({ token, description }) => (
                  <button
                    key={token}
                    type="button"
                    className={styles.tokenButton}
                    title={`Insert ${description}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertToken(token)}
                  >
                    {token}
                  </button>
                ))}
              </div>
            </div>

            {loadError && <p className={styles.error}>{loadError}</p>}

            <div className={styles.fields}>
              <label className={styles.fieldGroup}>
                <span>Subject</span>
                <input
                  ref={subjectRef}
                  className={styles.textInput}
                  value={subject}
                  maxLength={200}
                  onFocus={() => {
                    lastFieldRef.current = 'subject';
                  }}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </label>

              <label className={styles.fieldGroup}>
                <span>Email text</span>
                <textarea
                  ref={bodyRef}
                  className={styles.textArea}
                  value={body}
                  maxLength={50000}
                  rows={12}
                  onFocus={() => {
                    lastFieldRef.current = 'body';
                  }}
                  onChange={(event) => setBody(event.target.value)}
                />
                <small className={styles.fieldHint}>
                  {body.length}/50000 characters
                </small>
              </label>
            </div>

            {unknownTokens.length > 0 && (
              <p className={styles.tokenWarning}>
                Unknown code{unknownTokens.length > 1 ? 's' : ''}:{' '}
                {unknownTokens.join(', ')}. These will be sent as plain text —
                only [navn], [program] and [innlogging] are replaced
                automatically.
              </p>
            )}

            <div className={styles.preview}>
              <span className={styles.previewLabel}>
                Preview example ({previewValues.navn} / {previewValues.program})
              </span>
              <div className={styles.previewBox}>
                <p className={styles.previewSubject}>
                  {previewWelcomeEmail(subject, previewValues) || 'Subject'}
                </p>
                <p className={styles.previewBody}>
                  {previewWelcomeEmail(body, previewValues)}
                </p>
              </div>
            </div>
          </>
        )}

        <div className={styles.footer}>
          <Button variant="outlined" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <div className={styles.saveActions}>
            <Button
              variant="outlined"
              onClick={handleSaveAsDefault}
              disabled={isLoading || isSaving || !canSave}
            >
              {isSavingDefault ? 'Saving...' : 'Save as default'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveForCircle}
              disabled={isLoading || isSaving || !canSave}
            >
              {isSavingCircle ? 'Saving...' : 'Save for this circle'}
            </Button>
            <p className={styles.saveHint}>
              <strong>This circle</strong> only affects registrations here.{' '}
              <strong>Default</strong> is used by circles that have not saved
              their own welcome text.
            </p>
          </div>
        </div>
      </div>
    </MuiModal>
  );
};
